'use client';

import { FormField } from '@shared/components/shadcn';
import { Football, Slots, TicketStar } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ProductPreferences } from '../../../ProductPreferences';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import type { ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import type { FormValues } from '../types';

function ProductPreferencesStep() {
  const step = useTranslations('register.screens.productPreferences');
  const actions = useTranslations('register.actions');

  const { skipToStep, setStatus } = useMultistepForm();
  const form = useFormContext<FormValues>();

  const gamblingOptions: ToggleOption[] = [
    { value: 'lottery', label: step('lottery'), icon: <TicketStar />, trackingProps: { 'data-track-name': 'Lottery' } },
    { value: 'casino', label: step('casino-and-scratchcards'), icon: <Slots />, trackingProps: { 'data-track-name': 'Casino and Scratchcards' } },
    { value: 'sportsbook', label: step('sportsbook'), icon: <Football />, trackingProps: { 'data-track-name': 'Sportsbook' } },
  ];

  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);

  useEffect(() => {
    const initialPrefs = (form.getValues('productPreferences') ?? []).filter((pref: unknown): pref is string => typeof pref === 'string');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedPreferences(initialPrefs);
  }, [form]);

  const watchedPreferences = form.watch('productPreferences');
  useEffect(() => {
    const prefs = (watchedPreferences ?? []).filter((pref: unknown): pref is string => typeof pref === 'string');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedPreferences(prefs);
  }, [watchedPreferences]);

  const primaryButtonText = selectedPreferences.length === 0 ? step('selectAllAndContinue') : step('continueWithSelection');

  const handleContinue = async () => {
    const currentPreferences = form.getValues('productPreferences') || [];

    if (currentPreferences.length === 0) {
      const allValues = gamblingOptions.map(option => option.value);

      form.setValue('productPreferences', allValues, { shouldValidate: false });
      setSelectedPreferences(allValues);

      setStatus('has-preferences');
      skipToStep('channel-preferences');
    } else {
      setStatus('has-preferences');
      skipToStep('channel-preferences');
    }
  };

  const handleNoOffers = () => {
    form.setValue('productPreferences', [], { shouldValidate: false });
    setSelectedPreferences([]);

    form.setValue('channelPreferences', [], { shouldValidate: false });

    setStatus('opted-out-preferences');

    skipToStep('deposit-limits');
  };

  return (
    <FormField
      control={form.control}
      name="productPreferences"
      render={({ field }) => (
        <ProductPreferences
          value={field.value ?? []}
          onChange={field.onChange}
          options={gamblingOptions}
          error={form.formState.errors.productPreferences?.message?.toString()}
          onContinue={handleContinue}
          onNoOffers={handleNoOffers}
          continueButtonText={primaryButtonText}
          noOffersButtonText={actions('dontSend')}
          continueButtonTrackName={selectedPreferences.length === 0 ? 'All Options' : 'Selected Options'}
          fieldId="productPreferences-error"
          stepLayout={{
            title: step('title'),
            'data-testid': 'product-preferences-step',
            showLogo: true,
            showBackButton: true,
          }}
        />
      )}
    />
  );
}

export { ProductPreferencesStep };
